// Phase 122 — Library content-type palette.
//
// Locked colour map used everywhere a content type needs visual treatment:
// pill badges on Library cards, swatch dots in the Save-to-folder modal,
// optional left-edge accent stripes on tiles. Keep this file as the single
// source of truth — when we add a new artifact type, update here and the
// whole Library picks it up automatically.

export const TYPE_STYLES = {
  // Lessons (legacy + generator) — mint
  lesson: { bg: "#96ded1", fg: "#1c2d3d", label: "Lesson" },
  // Units — navy
  unit: { bg: "#1c2d3d", fg: "#ffffff", label: "Unit" },
  // Performance task / project — purple
  summative_task: { bg: "#8b5cf6", fg: "#ffffff", label: "PT / Project" },
  // Tests — orange (locked Phase 120)
  test: { bg: "#f4843b", fg: "#ffffff", label: "Test" },
  // Math worksheets — amber
  math_worksheet: { bg: "#f59e0b", fg: "#1c2d3d", label: "Worksheet" },
  // Math close-the-gap (math_worksheet w/ mode=close_the_gap) — coral
  math_worksheet_close_the_gap: {
    bg: "#fb7185",
    fg: "#ffffff",
    label: "Close the gap",
  },
  // Rich tasks (math_task_extended) — lime (user choice)
  math_task_extended: { bg: "#84cc16", fg: "#1c2d3d", label: "Rich task" },
  // Low-floor-high-ceiling (math_task_lfhc) — teal
  math_task_lfhc: { bg: "#14b8a6", fg: "#ffffff", label: "LFHC" },
  // Supply plans — slate
  supply_plan: { bg: "#64748b", fg: "#ffffff", label: "Supply" },
  // Math Engine umbrella for everything else
  math_activation: { bg: "#10b981", fg: "#ffffff", label: "Activation" },
  math_diagnostic: { bg: "#10b981", fg: "#ffffff", label: "Diagnostic" },
  math_lesson1: { bg: "#10b981", fg: "#ffffff", label: "Math lesson 1" },
  math_lesson2: { bg: "#10b981", fg: "#ffffff", label: "Math lesson 2" },
  math_exit_review: { bg: "#10b981", fg: "#ffffff", label: "Exit review" },
  math_unit_summary: { bg: "#10b981", fg: "#ffffff", label: "Unit summary" },
  math_unit_test: { bg: "#10b981", fg: "#ffffff", label: "Math unit test" },
  math_unit_review: { bg: "#10b981", fg: "#ffffff", label: "Unit review" },
  math_mid_unit_quiz: { bg: "#10b981", fg: "#ffffff", label: "Mid-unit quiz" },
  math_end_unit_test: { bg: "#10b981", fg: "#ffffff", label: "End-unit test" },
};

const FALLBACK = { bg: "#94a3b8", fg: "#1c2d3d", label: "Activity" };

/**
 * Resolve the visual treatment for a Library activity, taking the
 * close-the-gap sub-mode into account.
 *
 * @param {object} activity - The activity doc (must include `output_type`).
 *   For math_worksheet we also peek at `inputs.mode` to distinguish
 *   close-the-gap from a generic worksheet.
 * @returns {{bg: string, fg: string, label: string}}
 */
export function styleForActivity(activity) {
  if (!activity) return FALLBACK;
  const type = activity.output_type || "lesson";
  if (type === "math_worksheet") {
    const mode = activity.inputs?.mode || activity.mode || "";
    if (mode === "close_the_gap") return TYPE_STYLES.math_worksheet_close_the_gap;
    return TYPE_STYLES.math_worksheet;
  }
  return TYPE_STYLES[type] || FALLBACK;
}

/**
 * Just the display label for a type — used in legends / filters.
 */
export function labelForType(activity) {
  return styleForActivity(activity).label;
}
